import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { 
  Bookmark, FileText, Briefcase, Clock, MapPin, DollarSign, 
  Calendar, X, RefreshCw, ChevronRight, CheckCircle, XCircle,
  AlertCircle, Trash2, Eye, Building2, Send
} from 'lucide-react';

export default function JobCenter({ onClose, onViewJob }) {
  const [activeTab, setActiveTab] = useState('saved');
  const [savedJobs, setSavedJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobCenterData();
  }, []);

  const loadJobCenterData = async () => {
    setLoading(true);
    try {
      const [savedRes, appsRes] = await Promise.all([
        apiCall('/api/worker/saved-jobs'),
        apiCall('/api/worker/applications')
      ]);
      
      setSavedJobs(savedRes.jobs || []);
      setApplications(appsRes.applications || []);
    } catch (error) {
      console.error('Failed to load job center data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsaveJob = async (jobId) => {
    try {
      await apiCall(`/api/worker/saved-jobs/${jobId}`, {
        method: 'DELETE'
      });
      toast.success('Job removed from saved');
      setSavedJobs(prev => prev.filter(j => j._id !== jobId));
    } catch (error) {
      toast.error('Failed to remove job');
    }
  };

  const handleApplyToJob = async (jobId) => {
    try {
      await apiCall(`/api/jobs/${jobId}/apply`, {
        method: 'POST'
      });
      toast.success('Application submitted!');
      loadJobCenterData();
    } catch (error) {
      toast.error(error.message || 'Failed to apply');
    }
  };

  const handleWithdrawApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application?')) return;
    
    try {
      await apiCall(`/api/worker/applications/${applicationId}/withdraw`, {
        method: 'POST'
      });
      toast.success('Application withdrawn');
      loadJobCenterData();
    } catch (error) {
      toast.error('Failed to withdraw application');
    }
  };

  const getApplicationStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'accepted':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Accepted
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'withdrawn':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Withdrawn
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <Briefcase className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Job Center</h2>
                <p className="text-white/80">Manage your saved jobs and applications</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/20 rounded-xl transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Stats */}
          <div className="flex gap-4 mt-4">
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Saved Jobs</span>
              <p className="text-xl font-bold">{savedJobs.length}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Applications</span>
              <p className="text-xl font-bold">{applications.length}</p>
            </div>
            <div className="px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
              <span className="text-white/70 text-sm">Pending</span>
              <p className="text-xl font-bold">{applications.filter(a => a.status === 'pending').length}</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex gap-1 p-2">
            <button
              onClick={() => setActiveTab('saved')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === 'saved'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-saved-jobs"
            >
              <Bookmark className="w-4 h-4" />
              Saved Jobs ({savedJobs.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === 'applications'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              data-testid="tab-applications"
            >
              <FileText className="w-4 h-4" />
              Applications ({applications.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[55vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : (
            <>
              {/* Saved Jobs Tab */}
              {activeTab === 'saved' && (
                <div className="space-y-4">
                  {savedJobs.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <Bookmark className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <h3 className="font-semibold text-gray-900 mb-1">No Saved Jobs</h3>
                      <p className="text-gray-500 text-sm">Save jobs you're interested in to apply later</p>
                    </div>
                  ) : (
                    savedJobs.map((job) => (
                      <div 
                        key={job._id} 
                        className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition"
                        data-testid={`saved-job-${job._id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-start gap-4">
                              <div className="p-3 bg-blue-100 rounded-xl">
                                <Briefcase className="w-6 h-6 text-blue-600" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                                {job.employer_name && (
                                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <Building2 className="w-4 h-4" />
                                    {job.employer_name}
                                  </p>
                                )}
                                
                                <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" />
                                    ${job.hourly_rate}/hr
                                  </span>
                                  {job.location?.address && (
                                    <span className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      {job.location.address}
                                    </span>
                                  )}
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    {new Date(job.start_date).toLocaleDateString()}
                                  </span>
                                </div>
                                
                                <p className="text-xs text-gray-400 mt-2">
                                  Saved {new Date(job.saved_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-4">
                            <button
                              onClick={() => handleApplyToJob(job._id)}
                              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition"
                              data-testid={`apply-job-${job._id}`}
                            >
                              <Send className="w-4 h-4" />
                              Apply Now
                            </button>
                            <button
                              onClick={() => handleUnsaveJob(job._id)}
                              className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                            >
                              <Trash2 className="w-4 h-4" />
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Applications Tab */}
              {activeTab === 'applications' && (
                <div className="space-y-4">
                  {applications.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <h3 className="font-semibold text-gray-900 mb-1">No Applications</h3>
                      <p className="text-gray-500 text-sm">Your job applications will appear here</p>
                    </div>
                  ) : (
                    applications.map((app) => (
                      <div 
                        key={app._id} 
                        className={`bg-white rounded-xl border-2 p-5 transition ${
                          app.status === 'accepted' ? 'border-green-200 bg-green-50/30' :
                          app.status === 'rejected' ? 'border-red-200 bg-red-50/30' :
                          app.status === 'withdrawn' ? 'border-gray-200 bg-gray-50/50' :
                          'border-gray-200 hover:border-blue-200'
                        }`}
                        data-testid={`application-${app._id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-start gap-4">
                              <div className={`p-3 rounded-xl ${
                                app.status === 'accepted' ? 'bg-green-100' :
                                app.status === 'rejected' ? 'bg-red-100' :
                                'bg-blue-100'
                              }`}>
                                <Briefcase className={`w-6 h-6 ${
                                  app.status === 'accepted' ? 'text-green-600' :
                                  app.status === 'rejected' ? 'text-red-600' :
                                  'text-blue-600'
                                }`} />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <h3 className="text-lg font-semibold text-gray-900">{app.job_title}</h3>
                                  {getApplicationStatusBadge(app.status)}
                                </div>
                                {app.employer_name && (
                                  <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                                    <Building2 className="w-4 h-4" />
                                    {app.employer_name}
                                  </p>
                                )}
                                
                                <div className="flex flex-wrap gap-3 mt-3 text-sm text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <DollarSign className="w-4 h-4" />
                                    ${app.hourly_rate}/hr
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-4 h-4" />
                                    Applied {new Date(app.applied_at).toLocaleDateString()}
                                  </span>
                                </div>
                                
                                {app.status === 'rejected' && app.rejection_reason && (
                                  <p className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                                    {app.rejection_reason}
                                  </p>
                                )}
                                
                                {app.status === 'accepted' && (
                                  <p className="mt-2 text-sm text-green-600 bg-green-50 p-2 rounded-lg">
                                    🎉 Congratulations! Check your shifts for schedule details.
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex flex-col gap-2 ml-4">
                            {app.status === 'pending' && (
                              <button
                                onClick={() => handleWithdrawApplication(app._id)}
                                className="flex items-center gap-1 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                              >
                                <XCircle className="w-4 h-4" />
                                Withdraw
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
